import { useState } from 'react';
import { Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Modal } from '../../../../common/components/modals';
import { useLiquidatedOvertimeHours } from '../../hooks/useLiquidatedOvertimeHours';
import { LiquidatedOvertimeHourModel } from '../../models/liquidateOvertimeHours';
import { PreliquidationHourSummaryRequest } from '../../models/requests';
import OvertimeReasonsSelector from '../../../overtime-reasons/components/OvertimeReasonsSelector';
import { useModifyOvertimeHours } from '../../hooks/useModifyOvertimeHours';

export interface ModifyOverTimesModalProps {
    isOpen: boolean;
    onClose: () => void;
    reHidrate: () => Promise<void>;
    data: LiquidatedOvertimeHourModel;
}

export const ModifyOverTimesModal = ({
    data,
    isOpen,
    onClose,
    reHidrate,
}: ModifyOverTimesModalProps) => {
    const [errorMessage, setErrorMessage] = useState('');
    const [commetsValue, setCommentsValue] = useState('');
    const [hourValue, setHourValue] = useState(data.hour);
    const [minuteValue, setMinuteValue] = useState(data.minute);
    const [overtimeReasonId, setOvertimeReasonId] = useState<number>(0); 
    
    const modifyOVertimeHoursMutation = useModifyOvertimeHours();

    const { modifyOvertimeHours, isLoading } = useLiquidatedOvertimeHours();

    const handleHoursChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!/^(1[0-2]|[0-9])$/.test(event.target.value)) {
            return;
        }

        const value = parseInt(event.target.value, 10);
        if (!isNaN(value) && value >= 0 && value < 24) {
            setHourValue(value);
        }
    };

    // Función para manejar el cambio de los minutos
    const handleMinutesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!/^(5[0-9]|[0-4]?[0-9])$/.test(event.target.value)) {
            return;
        }

        const value = parseInt(event.target.value, 10);
        if (!isNaN(value) && value >= 0 && value < 60) {
            setMinuteValue(value);
        }
    };
    
    const handlerSubmit = async () => {
        try {
            if(overtimeReasonId === null || overtimeReasonId === 0){
                const errorText = 'Por favor selecciona un motivo de aprobación antes de continuar';
                toast.error(errorText);
                setErrorMessage(errorText);
                return;
            }

            const request: PreliquidationHourSummaryRequest = {
                comments: commetsValue,
                modifiedHour: hourValue,
                modifiedMinute: minuteValue,
                overtimeReasonId: overtimeReasonId
            };

            const response = await modifyOvertimeHours(
                request,
                data.preliquidationHourSummaryId
            );

            modifyOVertimeHoursMutation.mutate({ id: data.preliquidationHourSummaryId, model: request},{
                onError: () => {
                    toast.error(
                        response?.response?.data ??
                            'Ocurrió un error al modificar la hora extra'
                    );
                    setErrorMessage(
                        response?.response?.data ??
                            'Ocurrió un error al modificar la hora extra'
                    );
                },
                onSuccess: () => {
                    toast.success('Se modificó la hora extra correctamente');
                    setErrorMessage('');
                    onClose();
                    reHidrate();
                }
            })
        } catch (error) {
            toast.error('Ocurrió un error al modificar la hora extra');
            setErrorMessage('Ocurrió un error al modificar la hora extra');
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            modalTitle='Modificar horas extras seleccionadas'
            disableSubmit={!data.preliquidationHourSummaryId || isLoading}
            onSubmit={handlerSubmit}
            onClose={onClose}
            widthCustom='500px'
        >
            {!isLoading && errorMessage !== '' && (
                <Alert variant='danger'>{errorMessage}</Alert>
            )}
            <div className='my-3'>
                <label htmlFor={'comments'} className='form-label form-label'>
                    Tiempo aprobado
                </label>
                <div className='d-flex align-items-center gap-1'>
                    <input
                        className='form-control text-dark'
                        type='string'
                        value={hourValue}
                        onChange={handleHoursChange}
                        placeholder='00'
                    />
                    <div className='text-black fw-bolder'>:</div>
                    <input
                        type='string'
                        className='form-control text-dark'
                        value={minuteValue}
                        onChange={handleMinutesChange}
                        placeholder='00'
                    />
                </div>
            </div>

            <div className='my-3'>
                <label htmlFor={'comments'} className='form-label form-label'>
                    Comentarios
                </label>
                <textarea
                    className='form-control text-dark'
                    id='comments'
                    data-lt-tmp-id='lt-465430'
                    onChange={(e) => setCommentsValue(e.target.value)}
                    spellCheck={false}
                    data-gramm='false'
                    required={true}
                ></textarea>
            </div>
            
            <div className='my-3'> 
                <OvertimeReasonsSelector 
                    name="overtimeReasonId"
                    label="Razon de aprobación"
                    value={overtimeReasonId}
                    onChange={(_, v) => setOvertimeReasonId(v)}
                    required={true}
                /> 
            </div> 
        </Modal>
    );
};
